// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * DOM only. Not one byte of a user's file is interpreted in this file — it is
 * handed structures the worker already produced.
 *
 * Every piece of text that came out of a PDF reaches the page through
 * `textContent` or a `createTextNode`, never through `innerHTML`. A /Producer
 * string, a signer's name and an XMP software agent are all arbitrary text
 * from a file of unknown provenance; treating any of them as markup would let
 * a document rewrite this page.
 */

import { term } from './glossary.ts';
import { identifyProducer, CATEGORY_LABEL } from './pdf/producers.ts';
import { parsePdfDate } from './pdf/dates.ts';
import { describeChange } from './pdf/signatures.ts';
import { formatBytes } from './report.ts';
import type { Analysis, Finding, RevisionView } from './types.ts';

/* ── icons (Lucide, inlined — no font, no CDN) ─────────────────────── */

const PATHS: Record<string, string> = {
  layers:
    '<path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"/><path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"/><path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"/>',
  upload:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m17 8-5-5-5 5"/><path d="M12 3v12"/>',
  shield:
    '<path d="M20 13c0 5-3.5 7.5-7.7 8.9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1 1 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z"/>',
  alert:
    '<path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  check: '<path d="M21.8 10A10 10 0 1 1 17 3.3"/><path d="m9 11 3 3L22 4"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  x: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
  download:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/>',
  file: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>',
  rotate: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
  list: '<path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/>',
  eye: '<path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0"/><circle cx="12" cy="12" r="3"/>',
  trash: '<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  columns: '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M12 3v18"/>',
  pen: '<path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/>',
  clip: '<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>',
  send: '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/><path d="m21.854 2.147-10.94 10.939"/>',
};

export function icon(name: string, size = 16): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${
    PATHS[name] ?? ''
  }</svg>`;
}

function svgEl(name: string, size = 16): SVGElement {
  const wrap = document.createElement('div');
  wrap.innerHTML = icon(name, size);
  return wrap.firstElementChild as SVGElement;
}

/* ── shell ─────────────────────────────────────────────────────────── */

export function renderShell(root: HTMLElement) {
  root.innerHTML = `
    <header class="site-header">
      <span class="brand">${icon('layers', 20)} peelback</span>
      <nav class="header-nav" aria-label="About this tool">
        <button class="nav-btn" type="button" data-modal="how">How it works</button>
        <button class="nav-btn" type="button" data-modal="limits">What it can’t tell you</button>
        <button class="nav-btn" type="button" data-modal="privacy">Privacy</button>
        <button class="nav-btn" type="button" data-modal="about">About</button>
      </nav>
    </header>

    <main class="main-content">
      <section class="intro" id="intro">
        <h1>See every version a PDF still contains</h1>
        <p class="lede">
          Saving a PDF again does not rewrite it — the changes are added to the end, and every earlier
          version stays inside the file. peelback follows the ${term('xref', 'index chain')} back through
          them, hands each one to you as a PDF you can open, and reads what wrote it. It never counts
          ${term('eof', '%%EOF markers')}, which is what most advice tells you to do and is wrong on
          most real documents.
        </p>
        <button class="trust-badge" type="button" data-modal="privacy">
          ${icon('shield', 14)} Runs entirely in your browser. Your file is never uploaded.
        </button>
      </section>

      <section aria-labelledby="dz-title">
        <div class="dropzone" id="dropzone" role="button" tabindex="0" aria-describedby="dz-hint">
          ${icon('upload', 30)}
          <p class="dz-title" id="dz-title">Drop a PDF here</p>
          <p class="dz-hint" id="dz-hint">or tap to choose a file · paste with <kbd>Ctrl</kbd>/<kbd>⌘</kbd>+<kbd>V</kbd></p>
          <p class="dz-privacy">Nothing is uploaded. The file is read on this device and nowhere else.</p>
        </div>
        <input type="file" id="file-input" accept="application/pdf,.pdf" class="visually-hidden" tabindex="-1" />
        <div class="samples" id="samples">
          <span class="samples-label">Try a sample — the three marked <em>trap</em> each contain extra end-of-file markers, and peelback reports one save for all three:</span>
        </div>
      </section>

      <section class="progress-panel" id="progress" hidden aria-live="polite">
        <div class="progress-row">
          <span class="progress-phase" id="progress-phase">Working</span>
          <span class="progress-count" id="progress-count"></span>
        </div>
        <div class="progress-track"><div class="progress-fill" id="progress-fill"></div></div>
      </section>

      <section class="error-panel" id="error" hidden role="alert">
        <p class="error-title">${icon('alert', 16)} <span id="error-title-text">That file could not be read</span></p>
        <p class="error-detail" id="error-detail"></p>
        <button class="btn btn-sm" type="button" id="error-retry">${icon('rotate', 14)} Try another file</button>
      </section>

      <div id="results" hidden></div>
    </main>

    <button class="log-toggle" type="button" id="log-toggle" aria-expanded="false" aria-label="Event log">
      ${icon('list', 15)}<span class="log-toggle-label">Event log</span><span id="log-count">0</span>
    </button>

    <footer class="site-footer">
      <div class="footer-inner">
        <span>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
      </div>
    </footer>

    <p id="live-region" class="visually-hidden" role="status" aria-live="polite"></p>
    <div id="modal-host"></div>
  `;
}

/* ── progress + error ──────────────────────────────────────────────── */

export function setProgress(phase: string, done: number, total: number) {
  const panel = document.getElementById('progress');
  const fill = document.getElementById('progress-fill');
  const label = document.getElementById('progress-phase');
  const count = document.getElementById('progress-count');
  if (!panel || !fill || !label || !count) return;
  panel.hidden = false;
  label.textContent = phase;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  fill.style.width = `${pct}%`;
  count.textContent = total > 1 ? `${pct}%` : '';
}

export function hideProgress() {
  const panel = document.getElementById('progress');
  if (panel) panel.hidden = true;
}

export function showError(message: string, title = 'That file could not be read') {
  const panel = document.getElementById('error');
  const detail = document.getElementById('error-detail');
  const heading = document.getElementById('error-title-text');
  if (!panel || !detail || !heading) return;
  heading.textContent = title;
  detail.textContent = message;
  panel.hidden = false;
}

export function hideError() {
  const panel = document.getElementById('error');
  if (panel) panel.hidden = true;
}

export function toast(message: string) {
  const live = document.getElementById('live-region');
  if (live) live.textContent = message;
}

/* ── small builders ────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function panel(title: string, extra?: HTMLElement): { root: HTMLElement; body: HTMLElement } {
  const root = el('section', 'panel');
  const head = el('div', 'panel-head');
  head.append(el('h2', 'panel-title', title));
  if (extra) head.append(extra);
  const body = el('div', 'panel-body');
  root.append(head, body);
  return { root, body };
}

function button(label: string, iconName: string, className = 'btn btn-sm'): HTMLButtonElement {
  const b = el('button', className);
  b.type = 'button';
  b.append(svgEl(iconName, 14), document.createTextNode(` ${label}`));
  return b;
}

function dateText(raw: string | null): string | null {
  const parsed = parsePdfDate(raw);
  if (!parsed) return null;
  return parsed.epochMs === null ? `${parsed.raw} (not a readable date)` : parsed.display;
}

/* ── the headline ──────────────────────────────────────────────────── */

function headline(a: Analysis): HTMLElement {
  const root = el('div', 'verdict');
  const n = a.saveCount;

  root.append(
    el('p', 'verdict-headline', n === 1 ? 'This file was saved once.' : `This file was saved ${n} times.`),
  );

  const sub = el('p', 'verdict-sub');
  if (n === 1) {
    sub.textContent =
      'There is one index in it, so no earlier version of the document is inside these bytes. ' +
      'That does not mean it was never changed — printing a document, exporting it, or choosing “Save As” ' +
      'produces a brand-new file with no history in it at all.';
  } else {
    sub.textContent =
      `Each of the ${n - 1} earlier version${n === 2 ? '' : 's'} is still physically present in the file, ` +
      'and each one can be opened on its own. Signing, filling in a form, and adding a comment each add a save.';
  }
  root.append(sub);
  return root;
}

/**
 * The base rate, stated before anything else. This is not a disclaimer — it is
 * the measurement that decides how the number above should be read, and it is
 * never collapsed, never dismissable and never below the fold.
 */
function baseRate(a: Analysis): HTMLElement {
  const root = el('div', 'baserate');
  root.append(svgEl('alert', 17));
  const body = el('div');

  const first = el('p');
  first.append(
    el('strong', undefined, 'More than one saved version is the ordinary case. '),
    document.createTextNode(
      'Six PDFs published by the IRS, HUD and the US Government Publishing Office were measured while this tool was built. ' +
        'Five of the six carry more than one end-of-file marker — the thing every article tells you to count — and in each of ' +
        'those five at least one marker is not an earlier version at all. Where a real earlier version was there, it was a ' +
        'digital signature that had put it there. The more official a document is, the more likely it is to have been signed, ' +
        'and signing is a save.',
    ),
  );
  body.append(first);

  if (a.eofMarkerCount > a.saveCount) {
    const second = el('p');
    second.append(
      document.createTextNode('Counting “%%EOF” markers in a text editor — the method most articles recommend — would have said '),
      el('strong', undefined, String(a.eofMarkerCount)),
      document.createTextNode(
        ` for this file rather than ${a.saveCount}. The extra ones belong to web optimisation, to an attached file, or to text printed on a page.`,
      ),
    );
    body.append(second);
  }

  root.append(body);
  return root;
}

/* ── one saved version ─────────────────────────────────────────────── */

export interface RevisionHandlers {
  onDownload: (rev: RevisionView) => void;
  onView: (rev: RevisionView) => void;
}

function revisionCard(rev: RevisionView, a: Analysis, handlers: RevisionHandlers): HTMLElement {
  const card = el('article', 'rev-card');
  const usable = rev.structureOk && rev.renderOk !== false;
  if (!usable) card.classList.add('is-broken');

  const head = el('div', 'rev-head');
  head.append(el('span', 'rev-number', `Save ${rev.index} of ${a.saveCount}`));
  if (rev.index === a.saveCount) head.append(el('span', 'badge badge-current', 'the file as it is now'));
  if (!usable) head.append(el('span', 'badge badge-broken', 'could not be opened'));
  head.append(
    el(
      'span',
      'rev-size',
      `${formatBytes(rev.byteLength)}${rev.index > 1 ? ` · ${formatBytes(rev.addedBytes)} added` : ''} · ${
        rev.pageCount
      } page${rev.pageCount === 1 ? '' : 's'}`,
    ),
  );
  card.append(head);

  const body = el('div', 'rev-body');
  body.append(
    el(
      'p',
      'rev-what',
      `This save ${rev.changes.map(describeChange).join(', and ')}.`,
    ),
  );

  const dl = el('dl', 'field-grid');
  const add = (label: string, value: string | null, mono = false) => {
    if (!value) return;
    dl.append(el('dt', undefined, label));
    const dd = el('dd');
    if (mono) dd.append(el('span', 'mono', value));
    else dd.textContent = value;
    dl.append(dd);
  };

  add('Written by', rev.info.producer, true);
  add('Created in', rev.info.creator, true);
  add('Created', dateText(rev.info.creationDate));
  add('Modified', dateText(rev.info.modDate));
  add('Named author', rev.info.author);
  add('Title', rev.info.title);
  add(
    'Index',
    `${rev.kind === 'stream' ? 'cross-reference stream' : 'cross-reference table'}, ${
      rev.changedObjectCount
    } object${rev.changedObjectCount === 1 ? '' : 's'} written`,
  );
  if (dl.childElementCount > 0) body.append(dl);

  const fact = identifyProducer(rev.info.producer);
  if (fact) {
    const note = el('p', 'producer-note');
    note.append(
      el('strong', undefined, `${fact.label} — ${CATEGORY_LABEL[fact.category]}. `),
      document.createTextNode(`${fact.what} ${fact.benign}`),
    );
    body.append(note);
  } else if (rev.info.producer) {
    const note = el('p', 'producer-note');
    note.textContent =
      'This is not a producer string peelback recognises. That is not notable in itself — there are thousands of ' +
      'applications that write PDFs, and most of them are not in any list.';
    body.append(note);
  }

  if (!rev.structureOk) {
    const note = el('p', 'producer-note');
    note.textContent = `This version did not re-read on its own: ${rev.structureError ?? 'unknown reason'}. It is not offered for download.`;
    body.append(note);
  } else if (rev.renderOk === false) {
    const note = el('p', 'producer-note');
    note.textContent = `peelback's own reader accepted this version, but a second PDF engine rejected it: ${
      rev.renderError ?? 'unknown reason'
    }. It is not offered for download.`;
    body.append(note);
  } else if (rev.renderOk === null && rev.renderError) {
    // Unknown is not failed. The strict parse — which is the actual gate —
    // passed, so the file is still offered and the gap is stated instead.
    const note = el('p', 'producer-note');
    note.textContent = `A second opinion could not be obtained on this version: ${rev.renderError}. It is still offered, because peelback's own reader is what decides.`;
    body.append(note);
  }

  const actions = el('div', 'card-actions');
  if (usable && rev.index < a.saveCount) {
    const dl2 = button(`Download save ${rev.index}`, 'download', 'btn btn-sm btn-primary');
    dl2.addEventListener('click', () => handlers.onDownload(rev));
    actions.append(dl2);
  } else if (usable) {
    const dl2 = button('Download this version', 'download');
    dl2.addEventListener('click', () => handlers.onDownload(rev));
    actions.append(dl2);
  }
  if (usable) {
    const view = button('Show it beside another', 'columns');
    view.addEventListener('click', () => handlers.onView(rev));
    actions.append(view);
  }
  if (actions.childElementCount > 0) body.append(actions);

  card.append(body);
  return card;
}

/* ── signatures ────────────────────────────────────────────────────── */

function signaturePanel(a: Analysis): HTMLElement | null {
  if (a.signatures.length === 0) return null;
  const { root, body } = panel('Digital signatures');

  for (const sig of a.signatures) {
    const card = el('article', 'rev-card');
    const head = el('div', 'rev-head');
    head.append(
      el('span', 'rev-number', sig.total > 1 ? `Signature ${sig.order} of ${sig.total}` : 'Signature'),
    );
    head.append(el('span', 'rev-size', sig.subFilter ?? 'format not recorded'));
    card.append(head);

    const cardBody = el('div', 'rev-body');
    const dl = el('dl', 'field-grid');
    const add = (label: string, value: string | null, mono = false) => {
      if (!value) return;
      dl.append(el('dt', undefined, label));
      const dd = el('dd');
      if (mono) dd.append(el('span', 'mono', value));
      else dd.textContent = value;
      dl.append(dd);
    };
    add('Field', sig.fieldName);
    add('Name given', sig.signerName);
    add('Time given', dateText(sig.signedAt));
    add('Reason given', sig.reason);
    add('Place given', sig.location);
    add('Covered range', sig.byteRange ? `[${sig.byteRange.join(' ')}]` : 'none written', true);
    add('Signature value', `${formatBytes(sig.signatureValueBytes)}, sitting between the two covered spans`);
    cardBody.append(dl);

    const value = el('p', 'producer-note');
    value.innerHTML = `The space between the two covered spans holds the ${term(
      'bytrange',
      'signature’s own value',
    )} — a signature cannot cover itself. It is not concealed content, and anything describing it as a gap in the file is misreading it.`;
    cardBody.append(value);

    const after = el('p', 'rev-what');
    after.textContent = sig.afterDescription;
    cardBody.append(after);

    card.append(cardBody);
    body.append(card);
  }

  const caveat = el('p', 'action-note');
  caveat.textContent =
    'peelback reads the ranges a signature declares and compares them against the file. It does not check the certificate, ' +
    'and it cannot tell you whether a signature is valid or who holds the key. A PDF reader with the right trust settings can.';
  body.append(caveat);
  return root;
}

/* ── XMP history ───────────────────────────────────────────────────── */

function xmpPanel(a: Analysis): HTMLElement | null {
  if (a.xmp.history.length === 0) return null;
  const { root, body } = panel('The record the file keeps of itself');

  const lead = el('p', 'action-note');
  lead.innerHTML = `Some applications embed an ${term(
    'xmp',
    'XMP',
  )} history listing each save and what made it. It is written by the application, it can be incomplete, and most files have none at all — so its absence means nothing.`;
  body.append(lead);

  const scroll = el('div', 'table-scroll');
  const table = el('table', 'data');
  table.innerHTML = '<thead><tr><th>When</th><th>What</th><th>Software</th><th>Changed</th></tr></thead>';
  const tbody = el('tbody');
  for (const e of a.xmp.history) {
    const tr = el('tr');
    const when = el('td');
    when.append(el('code', undefined, e.when ?? '—'));
    tr.append(when);
    tr.append(el('td', undefined, e.action ?? '—'));
    tr.append(el('td', undefined, e.softwareAgent ?? '—'));
    const changed = el('td');
    changed.append(el('code', undefined, e.changed ?? '—'));
    tr.append(changed);
    tbody.append(tr);
  }
  table.append(tbody);
  scroll.append(table);
  body.append(scroll);
  return root;
}

/* ── findings ──────────────────────────────────────────────────────── */

const LEVEL_ICON: Record<string, string> = { good: 'check', warn: 'alert', bad: 'x', info: 'info' };

function findingsList(findings: Finding[]): HTMLElement {
  const list = el('ul', 'findings');
  for (const f of findings) {
    const li = el('li', 'finding');
    const mark = svgEl(LEVEL_ICON[f.level] ?? 'info', 16);
    mark.classList.add(`lv-${f.level}`);
    const text = el('div');
    text.append(el('span', 'finding-title', `${f.title}. `), el('span', 'finding-detail', f.detail));
    li.append(mark, text);
    list.append(li);
  }
  return list;
}

/* ── the results view ──────────────────────────────────────────────── */

export interface ResultHandlers extends RevisionHandlers {
  onReset: () => void;
  onSaveReport: () => void;
  onSaveCsv: () => void;
  onSaveApplicant: () => void;
  onForget: () => void;
  onCompareChange: () => void;
}

export function renderResults(analysis: Analysis, handlers: ResultHandlers): void {
  const host = document.getElementById('results');
  if (!host) return;

  const wrap = el('div', 'results');

  const head = el('div', 'result-head');
  const left = el('div');
  left.append(el('p', 'result-file', analysis.fileName));
  left.append(
    el(
      'p',
      'result-meta',
      `${formatBytes(analysis.fileBytes)} · PDF ${analysis.pdfVersion ?? '(version not recorded)'} · ${
        analysis.objectCount
      } objects`,
    ),
  );
  const resetBtn = button('Start over', 'rotate', 'btn btn-sm btn-ghost');
  resetBtn.addEventListener('click', handlers.onReset);
  head.append(left, resetBtn);
  wrap.append(head);

  wrap.append(headline(analysis));
  wrap.append(baseRate(analysis));

  /* Each saved version. */
  const revs = panel('Each saved version');
  const list = el('div', 'revisions');
  for (const rev of analysis.revisions) list.append(revisionCard(rev, analysis, handlers));
  revs.body.append(list);
  if (analysis.saveCount > 1) {
    const note = el('p', 'action-note');
    note.innerHTML = `Each download is the whole file as it stood at that ${term(
      'save',
      'save',
    )} — a complete PDF you can open, print and read, not an extract. Every one has been re-read from scratch by peelback's own parser and opened again by a separate PDF engine before being offered.`;
    revs.body.append(note);
  }
  wrap.append(revs.root);

  /* The side-by-side reader — the point of the whole tool when there is more
     than one version to compare. */
  if (analysis.saveCount > 1) {
    wrap.append(comparePanel(analysis, handlers));
  }

  const sigs = signaturePanel(analysis);
  if (sigs) wrap.append(sigs);

  const xmp = xmpPanel(analysis);
  if (xmp) wrap.append(xmp);

  if (analysis.attachments.length > 0) {
    const att = panel('Files attached to this document');
    const ul = el('ul', 'findings');
    for (const name of analysis.attachments) {
      const li = el('li', 'finding');
      li.append(svgEl('clip', 16));
      li.append(el('div', 'finding-detail', name));
      ul.append(li);
    }
    att.body.append(ul);
    const note = el('p', 'action-note');
    note.textContent =
      'Anything inside an attachment — its own saves, its own signature, its own metadata — belongs to that file and not to this one. ' +
      'peelback does not read any of it as this document’s history.';
    att.body.append(note);
    wrap.append(att.root);
  }

  if (analysis.findings.length > 0) {
    const f = panel('Notes on this file');
    f.body.append(findingsList(analysis.findings));
    wrap.append(f.root);
  }

  /* Exports. */
  const actions = panel('Take it away');
  const grid = el('div', 'action-grid');

  const row = el('div', 'action-row');
  const reportBtn = button('Save the full report', 'file', 'btn btn-primary');
  reportBtn.addEventListener('click', handlers.onSaveReport);
  const csvBtn = button('Save changes.csv', 'download');
  csvBtn.addEventListener('click', handlers.onSaveCsv);
  row.append(reportBtn, csvBtn);
  grid.append(row);

  const applicantNote = el('p', 'action-note');
  applicantNote.textContent =
    'If you are checking a document somebody gave you, they will never be told what you found and cannot answer it — ' +
    'a landlord or employer looking at a document handed to them directly is not a credit-reporting agency, so none of the ' +
    'machinery that normally gives a person that right applies. This is a copy written for them, in plain language, so they can.';
  grid.append(applicantNote);

  const row2 = el('div', 'action-row');
  const applicantBtn = button('Save a copy for the person who sent it', 'send');
  applicantBtn.addEventListener('click', handlers.onSaveApplicant);
  row2.append(applicantBtn);
  grid.append(row2);

  const forgetNote = el('p', 'action-note');
  forgetNote.textContent =
    'Nothing was uploaded and nothing was written to disk — but the file and every recovered version are in this tab’s memory ' +
    'until you close it. This clears them now.';
  grid.append(forgetNote);

  const row3 = el('div', 'action-row');
  const forgetBtn = button('Forget this file', 'trash');
  forgetBtn.addEventListener('click', handlers.onForget);
  row3.append(forgetBtn);
  grid.append(row3);

  actions.body.append(grid);
  wrap.append(actions.root);

  /* The last word on the page is the one that routes away from accusation. */
  const before = panel('Before you act on any of this');
  const beforeBody = el('div');
  beforeBody.innerHTML = `
    <p class="action-note">
      Nothing above is evidence that anybody did anything wrong, and the findings fire hardest on the
      documents most likely to be real — the signed, official ones.
    </p>
    <p class="action-note">
      <strong>If something does not add up, ask the organisation that issued it.</strong> A payroll
      department can confirm a payslip; a bank can send a statement directly or share it through open
      banking. That answers the question. A file’s bytes cannot.
    </p>
    <p class="action-note">
      Turning someone down for a home or a job on the basis of what you read here can create real legal
      exposure — in the United States a screening practice can be unlawful because of its effect even
      without any discriminatory intent. If you rely on any of it, tell the person what you saw and give
      them a chance to answer.
    </p>
  `;
  const limitsBtn = button('What this can’t tell you', 'info');
  limitsBtn.dataset.modal = 'limits';
  const beforeRow = el('div', 'action-row');
  beforeRow.append(limitsBtn);
  beforeBody.append(beforeRow);
  before.body.append(beforeBody);
  wrap.append(before.root);

  host.replaceChildren(wrap);
  host.hidden = false;
}

/* ── the side-by-side reader ───────────────────────────────────────── */

function comparePanel(analysis: Analysis, handlers: ResultHandlers): HTMLElement {
  const { root, body } = panel('Read two versions side by side');

  const lead = el('p', 'action-note');
  lead.textContent =
    'This is the part worth having. A finding about metadata is something you have to take on trust; two renderings of the same ' +
    'page with a different number on each is something you can see for yourself.';
  body.append(lead);

  const usable = analysis.revisions.filter((r) => r.structureOk && r.renderOk !== false);
  const controls = el('div', 'compare-controls');

  const makeSelect = (id: string, label: string, selected: number) => {
    const wrap = el('label', 'control');
    wrap.append(document.createTextNode(label));
    const select = el('select');
    select.id = id;
    for (const rev of usable) {
      const option = el('option', undefined, `Save ${rev.index}${rev.index === analysis.saveCount ? ' (now)' : ''}`);
      option.value = String(rev.index);
      if (rev.index === selected) option.selected = true;
      select.append(option);
    }
    select.addEventListener('change', handlers.onCompareChange);
    wrap.append(select);
    return wrap;
  };

  const first = usable[0]?.index ?? 1;
  const last = usable[usable.length - 1]?.index ?? analysis.saveCount;
  controls.append(makeSelect('compare-left', 'On the left', first));
  controls.append(makeSelect('compare-right', 'On the right', last));

  const pageWrap = el('label', 'control');
  pageWrap.append(document.createTextNode('Page'));
  const pageInput = document.createElement('input');
  pageInput.type = 'number';
  pageInput.id = 'compare-page';
  pageInput.min = '1';
  pageInput.value = '1';
  pageInput.max = String(Math.max(1, ...analysis.revisions.map((r) => r.pageCount)));
  pageInput.addEventListener('change', handlers.onCompareChange);
  pageWrap.append(pageInput);
  controls.append(pageWrap);

  body.append(controls);

  const grid = el('div', 'compare-grid');
  grid.id = 'compare-grid';
  grid.append(comparePane('compare-pane-left', 'Loading…'), comparePane('compare-pane-right', 'Loading…'));
  body.append(grid);

  const diff = el('div');
  diff.id = 'compare-diff';
  body.append(diff);

  return root;
}

function comparePane(id: string, placeholder: string): HTMLElement {
  const pane = el('div', 'compare-pane');
  pane.id = id;
  pane.append(el('p', 'compare-label', ''), el('div', 'compare-empty', placeholder));
  return pane;
}

export function renderComparePane(
  side: 'left' | 'right',
  label: string,
  dataUrl: string | null,
  message: string,
): void {
  const pane = document.getElementById(`compare-pane-${side}`);
  if (!pane) return;
  pane.replaceChildren();
  pane.append(el('p', 'compare-label', label));
  if (dataUrl) {
    const img = document.createElement('img');
    img.className = 'compare-page';
    img.src = dataUrl;
    img.alt = `Page rendering of ${label}`;
    img.loading = 'lazy';
    pane.append(img);
  } else {
    pane.append(el('div', 'compare-empty', message));
  }
}

/** A line-level difference between the two rendered pages. */
export function renderCompareDiff(leftText: string | null, rightText: string | null): void {
  const host = document.getElementById('compare-diff');
  if (!host) return;
  host.replaceChildren();

  if (leftText === null || rightText === null) {
    host.append(
      el(
        'p',
        'diff-note',
        'The text of these two pages could not be compared. The pictures above are still the real renderings.',
      ),
    );
    return;
  }

  const left = leftText.split('\n').map((l) => l.trim()).filter(Boolean);
  const right = rightText.split('\n').map((l) => l.trim()).filter(Boolean);
  const leftSet = new Set(left);
  const rightSet = new Set(right);
  const removed = left.filter((l) => !rightSet.has(l));
  const added = right.filter((l) => !leftSet.has(l));

  if (removed.length === 0 && added.length === 0) {
    host.append(
      el('p', 'diff-note', 'The text on this page is identical in both versions. Try another page, or look at the pictures above.'),
    );
    return;
  }

  host.append(
    el(
      'p',
      'diff-note',
      `The text on this page differs. ${removed.length} line${removed.length === 1 ? '' : 's'} present on the left ${
        removed.length === 1 ? 'is' : 'are'
      } not on the right, and ${added.length} ${added.length === 1 ? 'is' : 'are'} the other way round.`,
    ),
  );

  const pre = el('pre', 'diff-lines');
  for (const line of removed.slice(0, 60)) {
    const span = el('span', 'diff-del', `− ${line}\n`);
    pre.append(span);
  }
  for (const line of added.slice(0, 60)) {
    const span = el('span', 'diff-add', `+ ${line}\n`);
    pre.append(span);
  }
  host.append(pre);

  const note = el('p', 'action-note');
  note.textContent =
    'This compares the text a PDF reader extracts, line by line. A difference in wording or a figure will show up here; ' +
    'a change to a picture, a stamp or a scanned page will not, because there is no text in those to compare.';
  host.append(note);
}
